import { useApolloClient } from "@apollo/client";
import { Button, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { RiBroadcastLine } from "react-icons/ri";
import { LearnMore } from "src/components/Links/LearnMore";
import { publishMatchRun } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { evictMatchPublishEventCache } from "src/services/apolloCacheManager";

interface PublishLotteryPositionButtonProps {
  matchId: string;
}

export const PublishLotteryPositionButton = ({
  matchId,
}: PublishLotteryPositionButtonProps) => {
  const {
    confirm: confirmPublishLotteryPosition,
    confirmationDialog: publishLotteryPositionConfirmationDialog,
  } = useConfirmationDialog({
    header: "Save lottery position",
    body: (
      <Text>
        All lottery positions will immediately <b>become visible to staff</b>{" "}
        and impact the waitlist sorting. This{" "}
        <b>does not extend any offers to families</b>.
        <br />
        <br />
        Note: Waitlists are <b>subject to change sorting by tags</b>, and may
        not exactly match the lottery positions depending on the organization's
        settings.{" "}
        <LearnMore article="44716648319245-Saving-lottery-positions" />.
      </Text>
    ),
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Save",
    },
  });

  const toast = useAvelaToast();
  const apolloClient = useApolloClient();

  const handlePublishClick = useCallback(async () => {
    if (!(await confirmPublishLotteryPosition())) {
      return;
    }

    const toastId = toast({
      title: "Working hard",
      description: "Publishing lottery position",
      status: "loading",
      duration: null,
    });

    try {
      const result = await publishMatchRun({
        path: { matchRunId: matchId },
        body: {
          actions: {
            LotteryPosition: {},
          },
        },
      });

      toast.close(toastId);
      if (result.error) {
        toast.genericError();
        return;
      }
      evictMatchPublishEventCache(apolloClient.cache);
      if (result.data.data.LotteryPosition?.status === "PartiallyCompleted") {
        console.error(result.data.data.LotteryPosition.errors);
        toast({
          title: "Lottery position partially published",
          description:
            "Some lottery positions were published, but others could not be completed. Please review and try again, or contact support if the issue continues.",
          status: "warning",
          duration: null,
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Published",
        description: "The lottery position has been published",
        status: "success",
      });
    } catch (error) {
      toast.close(toastId);
      console.error(error);
      toast.genericError();
      return;
    }
  }, [apolloClient.cache, confirmPublishLotteryPosition, matchId, toast]);

  return (
    <>
      <Button
        aria-label="Publish lottery position"
        onClick={handlePublishClick}
        leftIcon={<RiBroadcastLine />}
      >
        Save lottery position
      </Button>
      {publishLotteryPositionConfirmationDialog}
    </>
  );
};
